(() => {
  const ELEMENT_NAME = "search-drawer";

  if (customElements.get(ELEMENT_NAME)) return;

  class SearchDrawer extends HTMLElement {
    // Below this the default view stays up, so a single stray character does
    // not replace popular searches with a near-random result set.
    static MIN_QUERY_LENGTH = 2;
    static DEBOUNCE_MS = 250;
    static ANIMATION_MS = 250;
    // Outer bound on how long a swallowed click is waited for. A pointerdown
    // that never becomes a click — a drag, a scroll, a cancelled touch — must
    // not leave the guard armed for an unrelated click later on.
    static CLICK_GUARD_MS = 500;

    #abortController = null;
    #fetchController = null;
    #panels = null;
    #panel = null;
    #overlay = null;
    #views = new Map();
    #header = null;
    #headerRoot = null;
    #resizeObserver = null;
    #debounceId = 0;
    #closeTimer = 0;
    #motionTimer = 0;
    #clickGuardTimer = 0;
    #isOpen = false;
    #suppressOpen = false;
    #activeInput = null;
    #lastQuery = null;
    #cache = new Map();
    #deferredPromise = null;

    connectedCallback() {
      this.#panels = this.querySelector("[data-search-drawer-panels]");
      this.#panel = this.querySelector("[data-search-drawer-panel]");
      this.#overlay = this.querySelector("[data-search-drawer-overlay]");
      this.#headerRoot = document.querySelector("roka-header");
      this.#header = document.querySelector("[data-header]");

      for (const view of this.querySelectorAll("[data-search-drawer-view]")) {
        this.#views.set(view.dataset.searchDrawerView, view);
      }

      if (!this.#panel) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      // Delegated from the document so the drawer keeps working after the
      // theme editor re-renders the header section that owns the field.
      this.#overlay?.addEventListener("click", this.#close, { signal });
      // Capture, so an Escape the drawer owns is settled before the header's
      // own document listener sees it. Both listen on `document`, so in the
      // bubble phase the order would come down to which element upgraded
      // first, and the loser would close the menu the drawer opened over.
      document.addEventListener("keydown", this.#handleKeydown, {
        signal,
        capture: true,
      });
      document.addEventListener("focusin", this.#handleFocusIn, { signal });
      document.addEventListener("input", this.#handleInput, { signal });
      document.addEventListener("click", this.#handleClick, { signal });
      document.addEventListener("pointerdown", this.#handlePointerDown, {
        signal,
      });
      // Capture: mouseenter does not bubble, and the mega menu must not be
      // edited to dismiss search. This runs before the menu's own hover open.
      document.addEventListener("mouseenter", this.#handleMegaMenuHover, {
        signal,
        capture: true,
      });
      window.addEventListener("resize", this.#syncPosition, { signal });
      window.addEventListener("scroll", this.#syncPosition, {
        signal,
        passive: true,
      });

      if (typeof ResizeObserver === "function") {
        this.#resizeObserver = new ResizeObserver(this.#syncPosition);
        this.#resizeObserver.observe(this.#panel);
      }

      this.#panels?.setAttribute("hidden", "");
      this.#syncClearButtons();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#fetchController?.abort();
      this.#fetchController = null;
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
      this.#clearDebounce();
      this.#clearCloseTimer();
      this.#clearMotionTimer();
      this.#releaseClickGuard();
      this.#unlockPage();
      // `#finishClose` owns this one and does not run on teardown.
      this.#setNavigationInert(false);
    }

    #activate(input) {
      this.#activeInput = input;
      this.#open();
      this.#requestResults(input.value);
    }

    // Mega menu opens on hover while the field can still hold focus. Closing
    // without leaving the field retriggers `#handleFocusIn` and the two
    // overlays reopen each other. Same suppress + blur as Escape.
    closeForMegaMenu() {
      if (!this.#isOpen) return;

      const input = this.#activeInput;

      this.#suppressOpen = true;
      this.#close();
      input?.blur();
      window.setTimeout(() => {
        this.#suppressOpen = false;
      }, 0);
    }

    #handleMegaMenuHover = (event) => {
      if (!this.#isOpen) return;
      if (!window.matchMedia("(min-width: 768px)").matches) return;

      const trigger = event.target.closest?.("[data-mega-menu-trigger]");

      if (!trigger) return;

      this.closeForMegaMenu();
    };

    #handleInput = (event) => {
      const input = event.target.closest?.("[data-header-search-input]");

      if (!input) return;

      this.#activate(input);
    };

    #handleClick = (event) => {
      const clear = event.target.closest?.("[data-header-search-clear]");

      if (clear) {
        this.#handleClear(event, clear);
        return;
      }

      // Re-opens after Escape without needing to blur and refocus the field.
      const input = event.target.closest?.("[data-header-search-input]");

      if (input) {
        this.#activate(input);
      }
    };

    // Clearing a typed query returns to the default view and keeps focus so the
    // shopper can retype; clearing an already-empty field dismisses the drawer.
    #handleClear(event, button) {
      const form = button.closest("form");
      const input = form?.querySelector("[data-header-search-input]");

      event.preventDefault();

      if (input && input.value !== "") {
        input.value = "";
        input.focus();
        this.#activate(input);
        return;
      }

      this.#close();
      input?.blur();
    }

    #handleKeydown = (event) => {
      if (event.key !== "Escape" || !this.#isOpen) return;

      const input = this.#activeInput;
      this.#close();

      // The mobile menu is deliberately left open behind the drawer so the
      // shopper keeps their place in the nav, so the Escape that dismissed
      // search must not travel on and close the menu underneath it too.
      event.stopPropagation();

      // Returning focus to the field must not re-open what Escape dismissed.
      this.#suppressOpen = true;
      input?.focus();
      window.setTimeout(() => {
        this.#suppressOpen = false;
      }, 0);
    };

    #handleFocusIn = (event) => {
      const input = event.target.closest?.("[data-header-search-input]");

      if (input) {
        if (!this.#suppressOpen) {
          this.#activate(input);
        }

        return;
      }

      if (!this.#isOpen) return;
      if (this.#isInsideDrawer(event.target)) return;

      this.#close();
    };

    #handlePointerDown = (event) => {
      // A gesture that produced no click leaves the guard armed. The start of
      // the next gesture is the proof that it never will, so release it here
      // rather than let it eat that gesture's own click.
      this.#releaseClickGuard();

      if (!this.#isOpen) return;
      if (this.#isInsideDrawer(event.target)) return;

      // Closing takes the scrim out of hit-testing in this same tick, so the
      // click that completes the tap is retargeted to whatever is underneath.
      // With the mobile menu open behind the drawer that is a nav link which
      // has just faded back in, and the tap meant to dismiss search would
      // navigate instead. Swallow exactly that one click.
      //
      // Only pointerdowns on the drawer's own furniture are guarded. A header
      // control is on top before and after the close, keeps the click it was
      // given, and must go on working in one tap.
      if (event.target instanceof Node && this.contains(event.target)) {
        this.#armClickGuard();
      }

      this.#close();
    };

    #armClickGuard() {
      document.addEventListener("click", this.#swallowClick, { capture: true });
      this.#clickGuardTimer = window.setTimeout(
        this.#releaseClickGuard,
        SearchDrawer.CLICK_GUARD_MS,
      );
    }

    // Capture on the document, so the click is stopped before it reaches the
    // link it was retargeted to rather than after.
    #swallowClick = (event) => {
      this.#releaseClickGuard();
      event.preventDefault();
      event.stopPropagation();
    };

    #releaseClickGuard = () => {
      document.removeEventListener("click", this.#swallowClick, {
        capture: true,
      });

      if (!this.#clickGuardTimer) return;

      window.clearTimeout(this.#clickGuardTimer);
      this.#clickGuardTimer = 0;
    };

    #isInsideDrawer(target) {
      if (!(target instanceof Element)) return false;

      return Boolean(
        target.closest("[data-search-drawer-panel]") ||
        target.closest("[data-header-search]") ||
        target.closest("[data-header-stacked-search]"),
      );
    }

    #open() {
      this.#clearCloseTimer();

      if (this.#isOpen) {
        this.#syncPosition();
        return;
      }

      this.#isOpen = true;
      this.#loadDeferredMarkup();
      this.#panels?.removeAttribute("hidden");
      this.#overlay?.removeAttribute("hidden");
      this.#syncPosition();
      this.#syncClearButtons();
      this.#markHeaderAnimating();
      this.#lockPage();
      this.#scheduleHeaderMotionRelease();

      if (this.#prefersReducedMotion()) {
        this.classList.add("search-drawer--open");
        return;
      }

      // Force layout so the panel animates in from its collapsed transform
      // instead of appearing already open.
      this.#panel?.getBoundingClientRect();
      requestAnimationFrame(() => {
        if (this.#isOpen) {
          this.classList.add("search-drawer--open");
        }
      });
    }

    #close = () => {
      if (!this.#isOpen) return;

      this.#isOpen = false;
      this.#fetchController?.abort();
      this.#fetchController = null;
      this.#clearDebounce();
      this.#markHeaderAnimating();
      this.classList.remove("search-drawer--open");
      this.#syncClearButtons();
      this.#unlockPage();
      this.#scheduleHeaderMotionRelease();
      this.#clearCloseTimer();

      if (this.#prefersReducedMotion()) {
        this.#finishClose();
        return;
      }

      this.#closeTimer = window.setTimeout(
        () => this.#finishClose(),
        SearchDrawer.ANIMATION_MS,
      );
    };

    #finishClose() {
      this.#clearCloseTimer();

      if (this.#isOpen) return;

      this.#panels?.setAttribute("hidden", "");
      // Held back until the panel has finished sliding away: hiding the scrim
      // in `#close` dropped it in frame 0, 250ms before the panel it belongs to.
      this.#overlay?.setAttribute("hidden", "");
      this.#setNavigationInert(false);
      this.#showView("default");
      this.#lastQuery = null;
    }

    #requestResults(rawValue) {
      const query = (rawValue ?? "").trim();

      this.#clearDebounce();
      this.#syncClearButtons();

      if (query.length < SearchDrawer.MIN_QUERY_LENGTH) {
        this.#fetchController?.abort();
        this.#fetchController = null;
        this.#lastQuery = null;
        this.#showView("default");
        return;
      }

      // Back to the query already on screen — but a request for the query the
      // shopper just deleted may still be in flight, and rendering it would
      // show results for text no longer in the field.
      if (query === this.#lastQuery) {
        this.#fetchController?.abort();
        this.#fetchController = null;
        return;
      }

      this.#debounceId = window.setTimeout(() => {
        this.#debounceId = 0;
        this.#fetchResults(query);
      }, SearchDrawer.DEBOUNCE_MS);
    }

    async #fetchResults(query) {
      const cached = this.#cache.get(query);

      if (cached !== undefined) {
        // Same reason as the early return above: a slower request for an
        // earlier query must not overwrite the cached view rendered here.
        this.#fetchController?.abort();
        this.#fetchController = null;
        this.#lastQuery = query;
        this.#renderResults(cached, query);
        return;
      }

      this.#fetchController?.abort();
      this.#fetchController = new AbortController();

      try {
        const response = await fetch(this.#buildUrl(query), {
          signal: this.#fetchController.signal,
        });

        if (!response.ok) return;

        const markup = await response.text();

        this.#cache.set(query, markup);
        this.#lastQuery = query;
        this.#renderResults(markup, query);
      } catch {
        // Aborted or offline — leave the current view in place.
      }
    }

    #buildUrl(query) {
      const url = new URL("/search/suggest", window.location.origin);

      url.searchParams.set("q", query);
      url.searchParams.set("section_id", this.dataset.resultsSection ?? "");
      url.searchParams.set(
        "resources[type]",
        this.dataset.resourceTypes ?? "product",
      );
      url.searchParams.set(
        "resources[limit]",
        this.dataset.resourceLimit ?? "4",
      );
      url.searchParams.set("resources[limit_scope]", "each");

      return url.toString();
    }

    #renderResults(markup, query) {
      const template = document.createElement("template");
      template.innerHTML = markup;

      const incoming = template.content.querySelector(
        "[data-predictive-search-results]",
      );
      const resultsView = this.#views.get("results");

      if (!incoming || !resultsView) return;

      if (Number(incoming.dataset.resultCount ?? 0) === 0) {
        this.#renderEmptyMessage(query);
        this.#showView("empty");
        return;
      }

      resultsView.replaceChildren(incoming);
      this.#showView("results");
    }

    #renderEmptyMessage(query) {
      const message = this.querySelector("[data-search-drawer-empty-message]");
      const template = message?.dataset.messageTemplate;

      if (!message || !template) return;

      const [before, after = ""] = template.split("[query]");
      const term = document.createElement("b");
      term.textContent = query;

      message.replaceChildren(
        document.createTextNode(before),
        term,
        document.createTextNode(after),
      );
    }

    #showView(name) {
      for (const [viewName, view] of this.#views) {
        view.toggleAttribute("hidden", viewName !== name);
      }

      this.#syncPosition();
    }

    #syncPosition = () => {
      const headerBottom = this.#header?.getBoundingClientRect().bottom ?? 0;
      const top = Math.max(0, headerBottom);

      this.style.setProperty("--search-drawer-top", `${top}px`);

      if (!this.#isOpen) return;

      // Full-viewport scrim below the header so the panel slide cannot leave a
      // gap between the drawer and the dimmed page while transforming.
      this.style.setProperty("--search-drawer-overlay-top", `${top}px`);
    };

    #syncClearButtons() {
      for (const button of document.querySelectorAll(
        "[data-header-search-clear]",
      )) {
        button.toggleAttribute("hidden", !this.#isOpen);
      }
    }

    // Deliberately no scroll lock and no scrollbar compensation here: the
    // page stays scrollable behind the scrim, the panel re-anchors itself on
    // scroll, and a scrollbar that never disappears is what keeps the header
    // geometry — including the search field's pinned left edge — unchanged.
    #lockPage() {
      const root = document.documentElement;

      root.classList.add("search-drawer-open");
      this.#headerRoot?.classList.add("header-wrapper--search-open");
      this.#header?.classList.add("header--search-open");
      this.#setIconsInert(true);
      this.#setNavigationInert(true);
    }

    #unlockPage() {
      document.documentElement.classList.remove("search-drawer-open");
      this.#headerRoot?.classList.remove("header-wrapper--search-open");
      this.#header?.classList.remove("header--search-open");
      this.#setIconsInert(false);
      // The navigation is released in `#finishClose` instead. Removing the
      // class above starts its 250ms fade back in, and a second tap during
      // that window would otherwise land on a link that is still invisible —
      // the exact mis-navigation the click guard exists to prevent, one tap
      // later than the guard covers.
    }

    // The icons fade over the full transition, so their `visibility: hidden` is
    // delayed to the end of it — which would leave account, wishlist and cart
    // reachable by keyboard while they shrink to nothing. `display: none` used
    // to drop them in the same frame, so `inert` restores that: out of the tab
    // order and the accessibility tree immediately, still painted for the fade.
    #setIconsInert(isInert) {
      const icons = this.#header?.querySelector(".header__icons");
      if (!icons) return;

      icons.inert = isInert;
    }

    // The mobile menu is left open behind the drawer, so its links are still
    // in the tab order and the accessibility tree while the header's
    // stylesheet fades them to `opacity: 0`. Tabbing out of the search field
    // landed on one of them, and a focus that lands outside the drawer closes
    // it — so the drawer's own results could not be reached by keyboard at
    // all. `inert` drops the whole navigation out of both, exactly as
    // `#setIconsInert` does for the header icons; the stylesheet's
    // `pointer-events: none` stays as belt and braces.
    //
    // Set in `#lockPage` and cleared in `#finishClose`, not in `#unlockPage`,
    // so the release runs on the same 250ms schedule as the fade back in.
    #setNavigationInert(isInert) {
      const navigation = this.#headerRoot?.querySelector(
        "header-mobile-navigation",
      );
      if (!navigation) return;

      navigation.inert = isInert;
    }

    // Flags the header for the length of the search transition. The header's
    // ResizeObserver watches both the field and the icon cluster, so without
    // this it would re-probe its layout on every frame of the collapse; the
    // flag makes it wait and sync once when the transition ends instead. It is
    // also the hook that keeps the collapsing icons clipped while they animate
    // back open, which the resting state deliberately does not do.
    #markHeaderAnimating() {
      if (!this.#header) return;
      if (this.#prefersReducedMotion()) return;

      this.#clearMotionTimer();
      this.#header.classList.add("header--search-animating");
    }

    // Started only once the state class has landed and the style flush has run,
    // so the window is anchored to the transition it guards rather than to the
    // statement before it. The class itself goes on first, so frame 0 of the
    // close is already clipped.
    #scheduleHeaderMotionRelease() {
      if (!this.#header) return;
      if (this.#prefersReducedMotion()) return;

      this.#motionTimer = window.setTimeout(() => {
        this.#motionTimer = 0;
        this.#header?.classList.remove("header--search-animating");
      }, this.#motionDuration());
    }

    // Read from the token the CSS animates on, so the flag cannot outlive or
    // undercut the transition it is guarding when that token changes.
    #motionDuration() {
      const raw = getComputedStyle(
        this.#header ?? document.documentElement,
      ).getPropertyValue("--search-motion-duration");
      const ms = parseFloat(raw);

      if (!Number.isFinite(ms) || ms <= 0) return SearchDrawer.ANIMATION_MS;

      return raw.trim().endsWith("ms") ? ms : ms * 1000;
    }

    #loadDeferredMarkup() {
      this.#hydrateExploreCards();

      if (this.#deferredPromise) return this.#deferredPromise;

      const root = this.querySelector("[data-search-drawer-products]");
      const handles = (root?.dataset.productHandles || "")
        .split(",")
        .map((handle) => handle.trim())
        .filter(Boolean);

      if (!root || handles.length === 0 || root.childElementCount > 0) {
        this.#deferredPromise = Promise.resolve();
        return this.#deferredPromise;
      }

      this.#deferredPromise = this.#ensureProductCardScript()
        .then(() =>
          Promise.all(handles.map((handle) => this.#fetchProductCard(handle))),
        )
        .then((cards) => {
          if (!root.isConnected) return;

          const fragment = document.createDocumentFragment();

          for (const card of cards) {
            if (card) fragment.append(card);
          }

          root.append(fragment);
        });

      return this.#deferredPromise;
    }

    #hydrateExploreCards() {
      const widths = "(min-width: 768px) 25vw, 50vw";

      for (const spec of this.querySelectorAll("[data-search-explore-image]")) {
        const media = spec.parentElement;

        if (!media || media.querySelector("img")) continue;

        const alt = spec.dataset.searchExploreAlt || "";
        const desktop = this.#exploreImage(
          spec.dataset.searchExploreImage,
          spec.dataset.searchExploreSrcset,
          widths,
          "search-explore-card__image search-explore-card__image--desktop",
          alt,
        );
        const mobile = this.#exploreImage(
          spec.dataset.searchExploreMobile,
          spec.dataset.searchExploreMobileSrcset,
          widths,
          "search-explore-card__image search-explore-card__image--mobile",
          alt,
        );

        if (desktop) media.append(desktop);
        if (mobile) media.append(mobile);
        spec.remove();
      }
    }

    #exploreImage(src, srcset, sizes, className, alt) {
      if (!src) return null;

      const image = document.createElement("img");

      image.className = className;
      image.alt = alt;
      image.sizes = sizes;
      if (srcset) image.srcset = srcset;
      image.src = src;

      return image;
    }

    #ensureProductCardScript() {
      if (customElements.get("product-card")) return Promise.resolve();

      const src = this.dataset.productCardScript;

      if (!src) return Promise.resolve();

      const existing = document.querySelector(`script[src="${src}"]`);

      if (existing) {
        return new Promise((resolve) => {
          if (customElements.get("product-card")) {
            resolve();
            return;
          }

          existing.addEventListener("load", () => resolve(), { once: true });
        });
      }

      return new Promise((resolve) => {
        const script = document.createElement("script");

        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.head.append(script);
      });
    }

    async #fetchProductCard(handle) {
      const base = window.Shopify?.routes?.root || "/";
      const url = `${base}products/${encodeURIComponent(handle)}?section_id=deferred-product-card`;

      try {
        const response = await fetch(url, { credentials: "same-origin" });

        if (!response.ok) return null;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");

        return doc.querySelector(".search-drawer__product");
      } catch {
        return null;
      }
    }

    #prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    #clearDebounce() {
      if (!this.#debounceId) return;

      window.clearTimeout(this.#debounceId);
      this.#debounceId = 0;
    }

    #clearCloseTimer() {
      if (!this.#closeTimer) return;

      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = 0;
    }

    #clearMotionTimer() {
      this.#header?.classList.remove("header--search-animating");

      if (!this.#motionTimer) return;

      window.clearTimeout(this.#motionTimer);
      this.#motionTimer = 0;
    }
  }

  customElements.define(ELEMENT_NAME, SearchDrawer);
})();
